// ui.ts
import type { SnapMode, ColorPreset, PaletteTokens } from './types';
import type { SnapMessages } from './i18n';

export interface LauncherItem {
  label: string;
  description: string;
}

export interface LauncherOptions {
  modes: SnapMode[];
  position: 'bottom-right' | 'bottom-left';
  dir?: 'ltr' | 'rtl';                   // explicit direction; falls back to host page dir
  accent?: string;                       // override accent only
  theme?: 'light' | 'dark';
  colorPreset?: ColorPreset;             // built-in palette
  palette?: Partial<PaletteTokens>;      // per-token overrides
  title: string;
  subtitle: string;
  error: LauncherItem;
  support: LauncherItem;
  onError: () => void;
  onSupport: () => void;
}

export interface LauncherHandle {
  open(): void;
  close(): void;
  toggle(): void;
  destroy(): void;
}

/* ----------------------------------------------------------------------------
 * "Aurora glass" design language — translucent frosted surfaces floating over
 * a soft drifting halo, modern sans-serif (Geist) with a Geist Mono accent,
 * themable per preset or per-token. One cohesive system across the launcher,
 * both dialogs and the toast.
 * -------------------------------------------------------------------------- */

const SVG_SPARK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.5l1.6 5.4 5.4 1.6-5.4 1.6L12 16.5l-1.6-5.4-5.4-1.6 5.4-1.6L12 2.5z"/><path d="M18.5 16l.7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7.7-2.3z"/></svg>';
const SVG_CLOSE =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const SVG_CAPTURE =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2"/><circle cx="12" cy="12" r="3"/></svg>';
const SVG_CHAT =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8 8 0 0 1-11.6 7.1L4 20l1.4-5.4A8 8 0 1 1 21 11.5Z"/></svg>';
const SVG_IMAGE =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2.5"/><circle cx="8.5" cy="9.5" r="1.6"/><path d="M21 16l-5-5L7 20"/></svg>';
const SVG_UPLOAD =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V4M7 9l5-5 5 5M5 20h14"/></svg>';
const SVG_SPINNER =
  '<svg class="tsnap-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 3a9 9 0 1 0 9 9"/></svg>';
const SVG_TRASH =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13"/></svg>';

const DISP = `'Geist','Inter var',ui-sans-serif,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,system-ui,sans-serif`;
const BODY = `'Geist','Inter var',ui-sans-serif,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,system-ui,sans-serif`;
const MONO = `'Geist Mono','JetBrains Mono',ui-monospace,SFMono-Regular,Menlo,Consolas,monospace`;

/** Load the widget's body/mono fonts once (scoped use, graceful fallbacks). */
function ensureFonts() {
  if (document.getElementById('tsnap-fonts')) return;
  const l = document.createElement('link');
  l.id = 'tsnap-fonts'; l.rel = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700;800&family=Geist+Mono:wght@400;500;600&display=swap';
  document.head.appendChild(l);
}

/* ---------- color system ---------- */

type Pair<T> = { light: T; dark: T };

const PRESETS: Record<ColorPreset, Pair<PaletteTokens>> = {
  aurora: {
    light: {
      accent: '#E0533A', accent2: '#FF7B8E', accentSoft: '#FFD9CF',
      glassBg: 'rgba(255,251,247,0.78)', surface: 'rgba(255,251,247,0.60)',
      border: 'rgba(40,20,10,0.08)', ink: '#1A130E', muted: '#6B5F54', faint: '#A0958A',
      overlay: 'rgba(28,18,10,0.34)', shadow: '0 28px 80px rgba(80,30,10,0.22)',
      haloA: 'rgba(255,170,140,0.65)', haloB: 'rgba(255,150,200,0.50)', haloC: 'rgba(255,210,170,0.55)'
    },
    dark: {
      accent: '#FF7A5B', accent2: '#FF6F9E', accentSoft: '#5A2418',
      glassBg: 'rgba(22,16,12,0.68)', surface: 'rgba(40,28,22,0.55)',
      border: 'rgba(255,210,190,0.10)', ink: '#F4ECE3', muted: '#A89B8E', faint: '#6E635A',
      overlay: 'rgba(10,5,2,0.62)', shadow: '0 30px 90px rgba(0,0,0,0.6)',
      haloA: 'rgba(255,130,90,0.40)', haloB: 'rgba(255,120,170,0.32)', haloC: 'rgba(220,90,60,0.32)'
    }
  },
  mint: {
    light: {
      accent: '#0FB39A', accent2: '#5BD5E8', accentSoft: '#C8F2EA',
      glassBg: 'rgba(248,255,253,0.78)', surface: 'rgba(238,250,247,0.60)',
      border: 'rgba(10,60,50,0.08)', ink: '#0C1F1B', muted: '#4F6A63', faint: '#92A39C',
      overlay: 'rgba(10,30,25,0.32)', shadow: '0 28px 80px rgba(10,80,70,0.22)',
      haloA: 'rgba(140,240,210,0.60)', haloB: 'rgba(140,205,255,0.50)', haloC: 'rgba(200,250,220,0.55)'
    },
    dark: {
      accent: '#33D6B8', accent2: '#5BC4E8', accentSoft: '#0F3A33',
      glassBg: 'rgba(10,22,20,0.68)', surface: 'rgba(20,38,34,0.55)',
      border: 'rgba(180,240,220,0.10)', ink: '#E4F4EF', muted: '#8AA39C', faint: '#5C726B',
      overlay: 'rgba(4,12,10,0.62)', shadow: '0 30px 90px rgba(0,0,0,0.6)',
      haloA: 'rgba(60,200,170,0.36)', haloB: 'rgba(80,180,220,0.32)', haloC: 'rgba(40,160,140,0.30)'
    }
  },
  cobalt: {
    light: {
      accent: '#3559E8', accent2: '#7B3DD9', accentSoft: '#CFDBFF',
      glassBg: 'rgba(249,251,255,0.78)', surface: 'rgba(238,243,252,0.60)',
      border: 'rgba(20,40,90,0.08)', ink: '#0D1430', muted: '#566184', faint: '#9099B0',
      overlay: 'rgba(15,25,55,0.34)', shadow: '0 28px 80px rgba(20,40,120,0.22)',
      haloA: 'rgba(140,165,255,0.60)', haloB: 'rgba(180,140,255,0.50)', haloC: 'rgba(110,150,250,0.50)'
    },
    dark: {
      accent: '#6B85FF', accent2: '#A678FF', accentSoft: '#1A2350',
      glassBg: 'rgba(12,16,30,0.70)', surface: 'rgba(22,28,52,0.55)',
      border: 'rgba(180,200,255,0.10)', ink: '#E6EAF6', muted: '#8A92AE', faint: '#5C6480',
      overlay: 'rgba(4,6,18,0.62)', shadow: '0 30px 90px rgba(0,0,0,0.6)',
      haloA: 'rgba(80,110,255,0.36)', haloB: 'rgba(140,90,255,0.30)', haloC: 'rgba(60,80,200,0.30)'
    }
  },
  violet: {
    light: {
      accent: '#7B3DD9', accent2: '#DC4DA8', accentSoft: '#E6D7FA',
      glassBg: 'rgba(252,250,255,0.78)', surface: 'rgba(245,240,252,0.60)',
      border: 'rgba(60,30,100,0.08)', ink: '#1A102C', muted: '#665680', faint: '#9D90B5',
      overlay: 'rgba(28,15,50,0.34)', shadow: '0 28px 80px rgba(70,30,130,0.22)',
      haloA: 'rgba(190,140,255,0.58)', haloB: 'rgba(255,140,210,0.48)', haloC: 'rgba(160,120,240,0.50)'
    },
    dark: {
      accent: '#A678FF', accent2: '#FF6FCE', accentSoft: '#2D1B52',
      glassBg: 'rgba(18,12,28,0.70)', surface: 'rgba(30,22,48,0.55)',
      border: 'rgba(200,170,255,0.10)', ink: '#EEE8F9', muted: '#9A8FB5', faint: '#65597D',
      overlay: 'rgba(8,4,16,0.62)', shadow: '0 30px 90px rgba(0,0,0,0.6)',
      haloA: 'rgba(140,80,240,0.36)', haloB: 'rgba(255,100,200,0.30)', haloC: 'rgba(110,70,200,0.30)'
    }
  },
  sunset: {
    light: {
      accent: '#F0833A', accent2: '#FF5A8B', accentSoft: '#FFDCBF',
      glassBg: 'rgba(255,251,246,0.78)', surface: 'rgba(252,243,232,0.60)',
      border: 'rgba(60,30,5,0.08)', ink: '#1F140A', muted: '#705B48', faint: '#A99480',
      overlay: 'rgba(30,18,8,0.34)', shadow: '0 28px 80px rgba(140,60,15,0.22)',
      haloA: 'rgba(255,180,120,0.60)', haloB: 'rgba(255,140,180,0.50)', haloC: 'rgba(255,160,180,0.45)'
    },
    dark: {
      accent: '#FFA255', accent2: '#FF6B8E', accentSoft: '#4A2510',
      glassBg: 'rgba(22,16,10,0.70)', surface: 'rgba(38,28,20,0.55)',
      border: 'rgba(255,210,170,0.10)', ink: '#F5ECDF', muted: '#A99685', faint: '#6E5F50',
      overlay: 'rgba(10,5,2,0.62)', shadow: '0 30px 90px rgba(0,0,0,0.6)',
      haloA: 'rgba(255,140,80,0.36)', haloB: 'rgba(255,120,150,0.30)', haloC: 'rgba(255,90,120,0.28)'
    }
  },
  midnight: {
    light: {
      accent: '#2A2D38', accent2: '#5B6276', accentSoft: '#DADCE3',
      glassBg: 'rgba(252,252,254,0.78)', surface: 'rgba(244,245,248,0.60)',
      border: 'rgba(20,25,40,0.08)', ink: '#0E1018', muted: '#5B6076', faint: '#9095A8',
      overlay: 'rgba(15,18,28,0.34)', shadow: '0 28px 80px rgba(15,20,40,0.20)',
      haloA: 'rgba(200,210,230,0.50)', haloB: 'rgba(220,225,240,0.45)', haloC: 'rgba(180,195,220,0.40)'
    },
    dark: {
      accent: '#D9DEF0', accent2: '#A8B0CC', accentSoft: '#1E222E',
      glassBg: 'rgba(12,14,22,0.72)', surface: 'rgba(22,26,38,0.55)',
      border: 'rgba(200,210,235,0.10)', ink: '#ECEFF8', muted: '#969CB2', faint: '#5D6378',
      overlay: 'rgba(4,5,10,0.62)', shadow: '0 30px 90px rgba(0,0,0,0.65)',
      haloA: 'rgba(120,140,180,0.28)', haloB: 'rgba(80,100,150,0.22)', haloC: 'rgba(100,120,170,0.24)'
    }
  },
  ink: {
    light: {
      accent: '#111418', accent2: '#3C424E', accentSoft: '#E5E7EB',
      glassBg: 'rgba(254,254,254,0.80)', surface: 'rgba(246,247,249,0.60)',
      border: 'rgba(15,20,28,0.08)', ink: '#0A0D12', muted: '#52596B', faint: '#8B92A2',
      overlay: 'rgba(10,12,18,0.34)', shadow: '0 28px 80px rgba(10,15,25,0.18)',
      haloA: 'rgba(210,215,225,0.50)', haloB: 'rgba(225,228,235,0.45)', haloC: 'rgba(195,200,215,0.40)'
    },
    dark: {
      accent: '#F4F6FA', accent2: '#B8BFCE', accentSoft: '#1A1D24',
      glassBg: 'rgba(10,11,15,0.74)', surface: 'rgba(20,22,28,0.55)',
      border: 'rgba(225,228,235,0.10)', ink: '#F2F4F8', muted: '#969BAA', faint: '#5C6172',
      overlay: 'rgba(3,4,8,0.65)', shadow: '0 30px 90px rgba(0,0,0,0.7)',
      haloA: 'rgba(140,150,170,0.24)', haloB: 'rgba(100,108,128,0.20)', haloC: 'rgba(160,165,180,0.20)'
    }
  }
};

interface ResolvedPalette extends PaletteTokens { isDark: boolean }

function resolvePalette(
  preset: ColorPreset = 'aurora',
  theme?: 'light' | 'dark',
  accentOverride?: string,
  paletteOverride?: Partial<PaletteTokens>
): ResolvedPalette {
  const isDark = theme ? theme === 'dark'
    : (matchMedia?.('(prefers-color-scheme: dark)').matches ?? false);
  const base = (PRESETS[preset] ?? PRESETS.aurora)[isDark ? 'dark' : 'light'];
  const merged: PaletteTokens = { ...base, ...(paletteOverride ?? {}) };
  if (accentOverride) merged.accent = accentOverride;
  return { ...merged, isDark };
}

/* ---------- shared chrome ---------- */

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/** Aurora halo background — three slow-drifting blurred orbs behind the panel. */
function auroraHtml() {
  return `<div class="tsnap-aurora" aria-hidden="true">
    <span class="tsnap-orb tsnap-orb-a"></span>
    <span class="tsnap-orb tsnap-orb-b"></span>
    <span class="tsnap-orb tsnap-orb-c"></span>
  </div>`;
}

function auroraCss(p: ResolvedPalette) {
  return `
  .tsnap-aurora{position:absolute;inset:-40% -20%;pointer-events:none;z-index:0;filter:blur(60px) saturate(140%);opacity:.9}
  .tsnap-orb{position:absolute;width:48%;height:60%;border-radius:50%;mix-blend-mode:${p.isDark ? 'screen' : 'multiply'}}
  .tsnap-orb-a{top:-10%;left:-10%;background:radial-gradient(circle at 30% 30%,${p.haloA},transparent 60%);animation:tsnap-drift-a 16s ease-in-out infinite alternate}
  .tsnap-orb-b{top:20%;right:-15%;background:radial-gradient(circle at 60% 50%,${p.haloB},transparent 60%);animation:tsnap-drift-b 22s ease-in-out infinite alternate}
  .tsnap-orb-c{bottom:-20%;left:25%;background:radial-gradient(circle at 50% 50%,${p.haloC},transparent 60%);animation:tsnap-drift-c 18s ease-in-out infinite alternate}
  @keyframes tsnap-drift-a{to{transform:translate(8%,6%) scale(1.08)}}
  @keyframes tsnap-drift-b{to{transform:translate(-6%,4%) scale(1.12)}}
  @keyframes tsnap-drift-c{to{transform:translate(4%,-5%) scale(1.06)}}
  @media (prefers-reduced-motion:reduce){.tsnap-orb{animation:none}}`;
}

/** Shared modal chrome (overlay / glass box / header / pill buttons). */
function modalChrome(p: ResolvedPalette, maxWidth: number): string {
  // subtle dot grid pattern — makes the glass legible on plain host backgrounds
  const dot = p.isDark ? 'rgba(255,255,255,0.045)' : 'rgba(15,20,40,0.06)';
  const dotPattern = `radial-gradient(circle at 1px 1px,${dot} 1px,transparent 0)`;
  return auroraCss(p) + `
  #tsnap-overlay{position:fixed;inset:0;background:${p.overlay};backdrop-filter:blur(8px) saturate(140%);-webkit-backdrop-filter:blur(8px) saturate(140%);
    z-index:2147483647;display:flex;align-items:center;justify-content:center;padding:20px;font-family:${BODY};
    animation:tsnap-fade .24s ease}
  #tsnap-overlay *{box-sizing:border-box;margin:0}
  #tsnap-box{--ac:${p.accent};--ac2:${p.accent2};--acs:${p.accentSoft};position:relative;width:min(${maxWidth}px,100%);max-height:92dvh;
    color:${p.ink};border-radius:24px;display:flex;flex-direction:column;overflow:hidden;
    animation:tsnap-pop .42s cubic-bezier(.2,.9,.25,1.2)}
  #tsnap-box::before{content:"";position:absolute;inset:0;border-radius:24px;background:
    ${dotPattern} 0 0/18px 18px,${p.glassBg};
    backdrop-filter:blur(32px) saturate(180%);-webkit-backdrop-filter:blur(32px) saturate(180%);
    border:1px solid ${p.border};
    box-shadow:${p.shadow},inset 0 1px 0 ${p.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.85)'},inset 0 -1px 0 ${p.isDark ? 'rgba(0,0,0,0.30)' : 'rgba(15,10,5,0.04)'};
    z-index:1;pointer-events:none}
  /* corner accent wash for vibrancy without painting the whole surface */
  #tsnap-box::after{content:"";position:absolute;inset:0;border-radius:24px;pointer-events:none;z-index:1;
    background:radial-gradient(120% 80% at 100% 0%,color-mix(in srgb,var(--ac2) 18%,transparent),transparent 55%),
               radial-gradient(110% 70% at 0% 100%,color-mix(in srgb,var(--ac) 15%,transparent),transparent 60%);
    mix-blend-mode:${p.isDark ? 'screen' : 'multiply'};opacity:${p.isDark ? '0.7' : '0.55'}}
  #tsnap-box > *{position:relative;z-index:2}
  #tsnap-hd{display:flex;gap:14px;align-items:center;padding:22px 24px 20px;border-bottom:1px solid ${p.border}}
  #tsnap-hd-ic{flex:none;width:46px;height:46px;border-radius:15px;display:grid;place-items:center;color:#fff;
    background:linear-gradient(135deg,var(--ac),var(--ac2));
    box-shadow:0 10px 26px color-mix(in srgb,var(--ac) 38%,transparent),0 4px 10px color-mix(in srgb,var(--ac2) 28%,transparent),inset 0 1px 0 rgba(255,255,255,0.30)}
  #tsnap-hd-ic svg{width:22px;height:22px}
  #tsnap-hd-tx{flex:1;min-width:0}
  #tsnap-kick{font-family:${MONO};font-size:10.5px;font-weight:500;letter-spacing:.16em;text-transform:uppercase;color:${p.muted};display:inline-flex;align-items:center;gap:8px}
  #tsnap-kick::before{content:"";width:6px;height:6px;border-radius:50%;
    background:linear-gradient(135deg,var(--ac),var(--ac2));box-shadow:0 0 10px color-mix(in srgb,var(--ac) 60%,transparent)}
  #tsnap-title{font-family:${DISP};font-weight:700;font-size:24px;letter-spacing:-.022em;line-height:1.1;margin-top:6px}
  #tsnap-sub{margin-top:5px;font-size:13.5px;color:${p.muted};line-height:1.5}
  #tsnap-close{flex:none;appearance:none;border:1px solid ${p.border};background:${p.surface};color:${p.muted};
    width:36px;height:36px;border-radius:12px;display:grid;place-items:center;cursor:pointer;
    backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);
    transition:background .18s ease,color .18s ease,transform .25s cubic-bezier(.2,.9,.25,1.2)}
  #tsnap-close svg{width:15px;height:15px}
  #tsnap-close:hover{background:color-mix(in srgb,var(--ac) 14%,${p.surface});color:${p.ink};transform:rotate(90deg)}
  #tsnap-actions{display:flex;gap:10px;justify-content:flex-end;align-items:center;padding:16px 24px;border-top:1px solid ${p.border}}
  .tsnap-btn{appearance:none;border:1px solid ${p.border};background:${p.surface};color:inherit;border-radius:13px;
    padding:11px 20px;font-family:${BODY};font-size:14px;font-weight:500;letter-spacing:-.008em;cursor:pointer;
    backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);
    transition:background .18s ease,border-color .18s ease,transform .14s ease,box-shadow .25s ease}
  .tsnap-btn:hover{background:color-mix(in srgb,var(--ac) 10%,${p.surface});border-color:color-mix(in srgb,var(--ac) 28%,${p.border})}
  .tsnap-btn:active{transform:translateY(1px)}
  /* vibrant gradient primary button — accent → accent2 */
  .tsnap-btn.primary{border-color:transparent;color:#fff;
    background:linear-gradient(135deg,var(--ac),var(--ac2));
    box-shadow:0 10px 26px color-mix(in srgb,var(--ac) 32%,transparent),0 4px 10px color-mix(in srgb,var(--ac2) 24%,transparent),inset 0 1px 0 rgba(255,255,255,0.22)}
  .tsnap-btn.primary:hover{transform:translateY(-1px);box-shadow:0 14px 34px color-mix(in srgb,var(--ac) 44%,transparent),0 6px 14px color-mix(in srgb,var(--ac2) 32%,transparent),inset 0 1px 0 rgba(255,255,255,0.28)}
  .tsnap-spin{animation:tsnap-rot .7s linear infinite}
  @keyframes tsnap-rot{to{transform:rotate(360deg)}}
  @keyframes tsnap-fade{from{opacity:0}to{opacity:1}}
  @keyframes tsnap-pop{from{opacity:0;transform:translateY(16px) scale(.96)}to{opacity:1;transform:none}}
  @media (prefers-reduced-motion:reduce){#tsnap-overlay,#tsnap-box{animation:none}}`;
}

/* ---------- launcher ---------- */

/**
 * Mounts the floating help launcher: a soft glass FAB that fans open into an
 * aurora-lit panel offering the enabled modes. Returns a handle to control it.
 */
export function mountLauncher(opts: LauncherOptions): LauncherHandle {
  ensureFonts();
  const doc = document;
  const p = resolvePalette(opts.colorPreset, opts.theme, opts.accent, opts.palette);
  // Explicit dir wins (so an Arabic widget is RTL even on an LTR host); else follow the host page.
  const rtl = opts.dir ? opts.dir === 'rtl'
    : (doc.documentElement.getAttribute('dir') || getComputedStyle(doc.body).direction) === 'rtl';
  const side = opts.position === 'bottom-left' || rtl ? 'left' : 'right';

  const root = doc.createElement('div');
  root.id = 'tsnap-launcher';
  root.style.cssText = [
    'all:initial', 'position:fixed', 'bottom:24px', `${side}:24px`,
    'z-index:2147483646', `font-family:${BODY}`, 'direction:' + (rtl ? 'rtl' : 'ltr')
  ].join(';');

  const dot = p.isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,20,40,0.06)';
  const dotPattern = `radial-gradient(circle at 1px 1px,${dot} 1px,transparent 0)`;

  const css = auroraCss(p) + `
  #tsnap-launcher *{box-sizing:border-box;margin:0;padding:0}
  #tsnap-launcher{--ac:${p.accent};--ac2:${p.accent2};--acs:${p.accentSoft};--gb:${p.glassBg};--sf:${p.surface};--ink:${p.ink};--mu:${p.muted};--fn:${p.faint};--ln:${p.border}}

  /* vibrant gradient FAB — accent → accent2, inline icon + label, soft pooled halo underneath */
  .tsl-fab{position:relative;display:inline-flex;align-items:center;gap:12px;height:64px;padding:0 30px;cursor:pointer;border:0;
    border-radius:32px;color:#fff;font-family:${BODY};font-size:15.5px;font-weight:600;letter-spacing:-.014em;
    background:linear-gradient(135deg,var(--ac),var(--ac2));
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.30),
      inset 0 -1px 0 rgba(0,0,0,0.10),
      0 14px 32px color-mix(in srgb,var(--ac) 38%,transparent),
      0 6px 14px color-mix(in srgb,var(--ac2) 26%,transparent),
      0 2px 6px rgba(0,0,0,0.12);
    transition:transform .3s cubic-bezier(.2,.9,.25,1.2),box-shadow .3s ease;float:${side};outline:none}
  /* colored halo pooled underneath — gives the FAB an obvious light-emitting presence */
  .tsl-fab::after{content:"";position:absolute;inset:-14px -10px -22px -10px;border-radius:36px;
    background:radial-gradient(70% 90% at 50% 100%,color-mix(in srgb,var(--ac2) 65%,transparent),transparent 70%);
    filter:blur(24px);opacity:.85;z-index:-1;pointer-events:none;transition:opacity .3s ease}
  .tsl-fab:hover{transform:translateY(-2px) scale(1.02);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.34),
      inset 0 -1px 0 rgba(0,0,0,0.12),
      0 22px 46px color-mix(in srgb,var(--ac) 48%,transparent),
      0 10px 22px color-mix(in srgb,var(--ac2) 38%,transparent),
      0 3px 10px rgba(0,0,0,0.16)}
  .tsl-fab:hover::after{opacity:1}
  .tsl-fab:focus-visible{outline:none;box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.30),
      0 14px 32px color-mix(in srgb,var(--ac) 38%,transparent),
      0 0 0 4px color-mix(in srgb,var(--ac2) 30%,transparent)}
  .tsl-fab:active{transform:translateY(0) scale(.98)}
  .tsl-fab .ic{flex:none;width:28px;height:28px;display:grid;place-items:center;transition:transform .42s cubic-bezier(.2,.9,.25,1.2)}
  .tsl-fab .ic svg{width:24px;height:24px;display:block;filter:drop-shadow(0 1px 1px rgba(0,0,0,0.18))}
  /* keep the pill the same size whether the panel is open or closed — the visible panel is the open cue, no morph needed */
  #tsnap-launcher.open .tsl-fab .ic{transform:scale(.92)}
  .tsl-lbl{white-space:nowrap}

  /* modern glass panel with subtle dot grid + corner accent washes */
  .tsl-panel{position:absolute;bottom:80px;${side}:0;width:368px;max-width:calc(100vw - 32px);
    color:var(--ink);border-radius:24px;overflow:hidden;
    transform-origin:bottom ${side};
    opacity:0;visibility:hidden;transform:translateY(18px) scale(.92);
    transition:opacity .28s ease,transform .42s cubic-bezier(.2,.9,.25,1.2),visibility .28s}
  .tsl-panel::before{content:"";position:absolute;inset:0;border-radius:24px;
    background:${dotPattern} 0 0/18px 18px,var(--gb);
    backdrop-filter:blur(32px) saturate(180%);-webkit-backdrop-filter:blur(32px) saturate(180%);
    border:1px solid var(--ln);
    box-shadow:${p.shadow},inset 0 1px 0 ${p.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.85)'},inset 0 -1px 0 ${p.isDark ? 'rgba(0,0,0,0.30)' : 'rgba(15,10,5,0.04)'};
    z-index:1;pointer-events:none}
  /* corner accent wash for vibrancy */
  .tsl-panel::after{content:"";position:absolute;inset:0;border-radius:24px;pointer-events:none;z-index:1;
    background:radial-gradient(120% 80% at 100% 0%,color-mix(in srgb,var(--ac2) 18%,transparent),transparent 55%),
               radial-gradient(110% 70% at 0% 100%,color-mix(in srgb,var(--ac) 15%,transparent),transparent 60%);
    mix-blend-mode:${p.isDark ? 'screen' : 'multiply'};opacity:${p.isDark ? '0.7' : '0.55'}}
  .tsl-panel > *{position:relative;z-index:2}
  #tsnap-launcher.open .tsl-panel{opacity:1;visibility:visible;transform:translateY(0) scale(1)}

  .tsl-hd{padding:24px 24px 18px;border-bottom:1px solid var(--ln);display:flex;gap:14px;align-items:flex-start}
  /* avatar-ring icon — like the reference's avatar with a soft gradient ring */
  .tsl-hd-ic{flex:none;width:48px;height:48px;border-radius:16px;padding:2px;
    background:linear-gradient(135deg,var(--ac),var(--ac2));
    box-shadow:0 8px 22px color-mix(in srgb,var(--ac) 36%,transparent),0 4px 10px color-mix(in srgb,var(--ac2) 24%,transparent)}
  .tsl-hd-ic-inner{width:100%;height:100%;border-radius:14px;display:grid;place-items:center;color:#fff;
    background:linear-gradient(135deg,var(--ac),var(--ac2))}
  .tsl-hd-ic-inner svg{width:22px;height:22px;filter:drop-shadow(0 1px 1px rgba(0,0,0,0.18))}
  .tsl-hd-tx{flex:1;min-width:0}
  .tsl-kick{display:flex;align-items:center;gap:8px;font-family:${MONO};font-size:10.5px;font-weight:500;
    letter-spacing:.16em;text-transform:uppercase;color:var(--mu)}
  .tsl-kick::before{content:"";width:6px;height:6px;border-radius:50%;
    background:linear-gradient(135deg,var(--ac),var(--ac2));box-shadow:0 0 10px color-mix(in srgb,var(--ac) 60%,transparent)}
  .tsl-title{font-family:${DISP};margin-top:8px;font-size:24px;font-weight:700;letter-spacing:-.024em;line-height:1.1}
  .tsl-sub{margin-top:6px;font-size:13px;color:var(--mu);line-height:1.5;max-width:260px}

  /* chip-style action cards with subtle gradient hover */
  .tsl-list{padding:10px 10px 6px}
  .tsl-item{position:relative;overflow:hidden;width:100%;display:grid;grid-template-columns:1fr auto;align-items:center;gap:12px;
    padding:14px 16px;border:1px solid var(--ln);border-radius:16px;cursor:pointer;text-align:start;
    background:${p.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.45)'};
    color:inherit;font:inherit;font-family:${BODY};
    backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);
    transition:background .2s ease,border-color .2s ease,transform .2s ease,box-shadow .25s ease;
    opacity:0;transform:translateY(10px)}
  .tsl-item+.tsl-item{margin-top:6px}
  .tsl-item::before{content:"";position:absolute;inset:0;border-radius:16px;pointer-events:none;
    background:linear-gradient(135deg,color-mix(in srgb,var(--ac) 10%,transparent),color-mix(in srgb,var(--ac2) 10%,transparent));
    opacity:0;transition:opacity .22s ease}
  #tsnap-launcher.open .tsl-item{animation:tsl-rise .46s cubic-bezier(.2,.9,.25,1.2) forwards}
  #tsnap-launcher.open .tsl-item:nth-of-type(1){animation-delay:.08s}
  #tsnap-launcher.open .tsl-item:nth-of-type(2){animation-delay:.16s}
  .tsl-item:hover{border-color:color-mix(in srgb,var(--ac) 32%,var(--ln));transform:translateY(-1px);
    box-shadow:0 8px 22px color-mix(in srgb,var(--ac) 14%,transparent),0 2px 6px rgba(0,0,0,0.06)}
  .tsl-item:hover::before{opacity:1}
  .tsl-item:hover .tsl-arr{transform:translateX(${rtl ? '-4px' : '4px'});opacity:1;color:var(--ac)}
  .tsl-it-txt{position:relative;z-index:1;min-width:0;display:flex;flex-direction:column;gap:3px}
  .tsl-it-title{font-family:${DISP};font-size:16px;font-weight:600;letter-spacing:-.012em}
  .tsl-it-desc{font-size:12.5px;color:var(--mu);line-height:1.45}
  .tsl-arr{position:relative;z-index:1;font-size:18px;color:var(--fn);opacity:.7;transition:transform .22s ease,opacity .22s ease,color .18s ease;transform:${rtl ? 'scaleX(-1)' : 'none'}}
  /* pad the list bottom in place of the dropped footer so the panel ends cleanly */
  .tsl-list{padding-bottom:14px}

  @keyframes tsl-rise{to{opacity:1;transform:translateY(0)}}
  @media (prefers-reduced-motion:reduce){.tsl-fab,.tsl-panel,.tsl-item,.tsl-arr{transition:none!important;animation:none!important;opacity:1!important;transform:none!important}}`;

  const style = doc.createElement('style'); style.textContent = css;

  const itemHtml = (mode: SnapMode, item: LauncherItem) =>
    `<button class="tsl-item" data-mode="${mode}">
      <span class="tsl-it-txt"><span class="tsl-it-title">${escapeHtml(item.label)}</span><span class="tsl-it-desc">${escapeHtml(item.description)}</span></span>
      <span class="tsl-arr">${rtl ? '←' : '→'}</span>
    </button>`;

  const items = opts.modes.map(m => itemHtml(m, m === 'error' ? opts.error : opts.support)).join('');

  root.innerHTML = `
    <div class="tsl-panel" role="dialog" aria-label="${escapeHtml(opts.title)}">
      ${auroraHtml()}
      <div class="tsl-hd">
        <div class="tsl-hd-ic"><div class="tsl-hd-ic-inner">${SVG_SPARK}</div></div>
        <div class="tsl-hd-tx">
          <div class="tsl-kick">${escapeHtml(opts.subtitle)}</div>
          <div class="tsl-title">${escapeHtml(opts.title)}</div>
          <div class="tsl-sub">Pick how we can help — we'll attach the page context automatically.</div>
        </div>
      </div>
      <div class="tsl-list">${items}</div>
    </div>
    <button class="tsl-fab" aria-label="${escapeHtml(opts.title)}" aria-expanded="false">
      <span class="ic">${SVG_SPARK}</span><span class="tsl-lbl">Help</span>
    </button>`;

  doc.head.appendChild(style);
  doc.body.appendChild(root);

  const fab = root.querySelector('.tsl-fab') as HTMLButtonElement;
  let isOpen = false;
  const open = () => { isOpen = true; root.classList.add('open'); fab.setAttribute('aria-expanded', 'true'); };
  const close = () => { isOpen = false; root.classList.remove('open'); fab.setAttribute('aria-expanded', 'false'); };
  const toggle = () => (isOpen ? close() : open());

  fab.addEventListener('click', toggle);
  root.querySelectorAll('.tsl-item').forEach(el => {
    el.addEventListener('click', () => {
      const mode = (el as HTMLElement).dataset.mode as SnapMode;
      close();
      if (mode === 'error') opts.onError(); else opts.onSupport();
    });
  });
  const onDocClick = (e: MouseEvent) => { if (isOpen && !root.contains(e.target as Node)) close(); };
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) close(); };
  doc.addEventListener('click', onDocClick);
  doc.addEventListener('keydown', onKey);

  return {
    open, close, toggle,
    destroy() { doc.removeEventListener('click', onDocClick); doc.removeEventListener('keydown', onKey); root.remove(); style.remove(); }
  };
}

/** Briefly hide the widget's own overlays so a screen capture shows the host page, not our UI. */
function withWidgetHidden<T>(fn: () => Promise<T>): Promise<T> {
  const sel = ['#tsnap-overlay', '#tsnap-launcher'];
  const hidden: HTMLElement[] = [];
  sel.forEach(s => document.querySelectorAll<HTMLElement>(s).forEach(el => {
    if (el.style.visibility !== 'hidden') { hidden.push(el); el.style.visibility = 'hidden'; }
  }));
  const restore = () => hidden.forEach(el => { el.style.visibility = ''; });
  return new Promise<T>((resolve, reject) => {
    requestAnimationFrame(() => requestAnimationFrame(async () => {
      try { resolve(await fn()); } catch (e) { reject(e); } finally { restore(); }
    }));
  });
}

/* ---------- bug-report popup ---------- */

export interface PopupOptions {
  screenshotUrl?: string | null;
  /** Cross-system resource this error is about (e.g. an Order). Shown as a meta row. */
  link?: { type: string; id: string; label?: string; status?: string } | null;
  onSubmit: (notes: string, screenshot?: Blob | null) => void;
  onCancel: () => void;
  onCaptureScreen?: () => Promise<Blob | null>;
  colorPreset?: ColorPreset;
  theme?: 'light' | 'dark';
  accent?: string;
  palette?: Partial<PaletteTokens>;
  messages?: SnapMessages;
  dir?: 'ltr' | 'rtl';
}

export function openPopup(snapshotJsonText: string, opts: PopupOptions) {
  ensureFonts();
  const doc = document;
  const p = resolvePalette(opts.colorPreset, opts.theme, opts.accent, opts.palette);
  const overlay = doc.createElement('div');
  overlay.id = 'tsnap-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');

  const dir = opts.dir || doc.documentElement.getAttribute('dir') || (getComputedStyle(doc.body).direction || 'ltr');
  const M = opts.messages;

  let parsed: any = null; try { parsed = JSON.parse(snapshotJsonText); } catch {}
  const url = parsed?.request?.url || parsed?.page?.url || location.href;
  const statusNum = Number(parsed?.response?.status);
  const st = String(parsed?.response?.status ?? (parsed?.error ? 'Runtime' : '—'));
  const when = parsed?.page?.capturedAt ? new Date(parsed.page.capturedAt).toLocaleString() : new Date().toLocaleString();
  const tone = !Number.isFinite(statusNum) ? 'warn'
    : statusNum >= 500 ? 'bad' : statusNum >= 400 ? 'warn' : statusNum >= 200 ? 'ok' : 'warn';
  const toneColor = tone === 'bad' ? '#E0533A' : tone === 'ok' ? '#22C39A' : '#F0A23A';

  // Optional "linked resource" row (e.g. the Order this error happened on).
  const link = opts.link && opts.link.type && opts.link.id ? opts.link : null;
  const linkKey = link ? escapeHtml((link.type || 'Linked').toUpperCase()) : '';
  const linkVal = link ? escapeHtml(link.label || link.id) : '';
  const linkTag = link && link.status ? `<span class="tsnap-tag">${escapeHtml(link.status)}</span>` : '';
  const linkRow = link
    ? `<div class="tsnap-kv"><span class="k">${linkKey}</span><span class="v" title="${linkVal}">${linkVal}${linkTag}</span></div>`
    : '';

  const css = modalChrome(p, 880) + `
  #tsnap-body{display:grid;grid-template-columns:1fr 330px;gap:16px;padding:18px 22px;overflow:auto}
  @media (max-width:820px){#tsnap-body{grid-template-columns:1fr}}
  #tsnap-summary{display:grid;gap:14px;align-content:start;min-width:0}
  #tsnap-meta{display:grid;gap:0;border:1px solid ${p.border};border-radius:13px;overflow:hidden;background:${p.surface};
    backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}
  .tsnap-kv{display:grid;grid-template-columns:84px 1fr;gap:10px;align-items:center;padding:12px 14px;font-family:${BODY};font-size:13px}
  .tsnap-kv+.tsnap-kv{border-top:1px solid ${p.border}}
  .tsnap-kv .k{font-family:${MONO};color:${p.faint};text-transform:uppercase;letter-spacing:.12em;font-size:10.5px;font-weight:500}
  .tsnap-kv .v{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:${p.ink}}
  .tsnap-pill{display:inline-flex;align-items:center;gap:8px;font-weight:500;color:${p.ink}}
  .tsnap-pill i{width:8px;height:8px;border-radius:50%;background:${toneColor};box-shadow:0 0 10px ${toneColor}80}
  .tsnap-kv .v .tsnap-tag{display:inline-block;margin-inline-start:8px;padding:1px 9px;border-radius:999px;
    border:1px solid color-mix(in srgb,var(--ac) 35%,${p.border});background:color-mix(in srgb,var(--ac) 10%,transparent);
    color:var(--ac);font:10px/1.7 ${MONO};letter-spacing:.04em;vertical-align:middle}
  .tsnap-field{display:grid;gap:8px}
  .tsnap-lbl{font-family:${MONO};font-size:10.5px;font-weight:500;letter-spacing:.14em;text-transform:uppercase;color:${p.muted}}
  #tsnap-notes{width:100%;min-height:110px;resize:vertical;background:${p.surface};color:inherit;
    border:1px solid ${p.border};border-radius:13px;padding:12px 14px;font-family:${BODY};font-size:14px;line-height:1.5;
    backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);
    transition:border-color .16s ease,box-shadow .16s ease}
  #tsnap-notes::placeholder{color:${p.faint}}
  #tsnap-notes:focus{outline:none;border-color:var(--ac);box-shadow:0 0 0 4px color-mix(in srgb,var(--ac) 14%,transparent)}
  #tsnap-details{border:1px solid ${p.border};border-radius:13px;overflow:hidden;background:${p.surface};
    backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}
  #tsnap-details summary{list-style:none;cursor:pointer;padding:12px 14px;font-family:${MONO};font-size:10.5px;font-weight:500;
    letter-spacing:.12em;text-transform:uppercase;color:${p.muted};display:flex;align-items:center;gap:8px;user-select:none}
  #tsnap-details summary::-webkit-details-marker{display:none}
  #tsnap-details summary::before{content:"+";font-size:14px;color:var(--ac);transition:transform .22s ease}
  #tsnap-details[open] summary::before{transform:rotate(45deg)}
  #tsnap-pre{margin:0;padding:14px;max-height:200px;overflow:auto;white-space:pre-wrap;word-break:break-word;
    font:11.5px/1.55 ${MONO};color:${p.ink};border-top:1px solid ${p.border}}
  #tsnap-shotwrap{display:grid;gap:10px;align-content:start}
  #tsnap-sshot{position:relative;border:1px solid ${p.border};border-radius:14px;overflow:hidden;background:${p.surface};
    min-height:178px;display:flex;align-items:center;justify-content:center;padding:8px;
    backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}
  #tsnap-sshot img{width:100%;display:block;border-radius:8px;border:1px solid ${p.border}}
  .tsnap-empty{display:grid;justify-items:center;gap:12px;padding:26px 16px;text-align:center;color:${p.faint}}
  .tsnap-empty svg{width:32px;height:32px;opacity:.6}
  .tsnap-empty span{font-family:${BODY};font-size:12.5px;max-width:200px;line-height:1.5}
  #tsnap-capture{width:100%;display:inline-flex;align-items:center;justify-content:center;gap:8px;
    border:1px dashed color-mix(in srgb,var(--ac) 45%,${p.border});background:color-mix(in srgb,var(--ac) 7%,transparent);color:var(--ac);
    border-radius:13px;padding:11px 12px;font-family:${BODY};font-size:13px;font-weight:500;cursor:pointer;
    transition:background .18s ease,border-color .18s ease}
  #tsnap-capture svg{width:16px;height:16px}
  #tsnap-capture:hover:not(:disabled){background:color-mix(in srgb,var(--ac) 12%,transparent);border-style:solid}
  #tsnap-capture:disabled{opacity:.6;cursor:default}
  .tsnap-hint{font-family:${MONO};font-size:10px;letter-spacing:.06em;color:${p.faint};text-align:center;line-height:1.5}
  `;

  const style = doc.createElement('style'); style.textContent = css; doc.head.appendChild(style);
  const box = doc.createElement('div'); box.id = 'tsnap-box'; box.setAttribute('dir', dir);

  box.innerHTML = `
    ${auroraHtml()}
    <div id="tsnap-hd">
      <div id="tsnap-hd-ic">${SVG_CAPTURE}</div>
      <div id="tsnap-hd-tx">
        <div id="tsnap-kick">Bug report · ${escapeHtml(String(st))}</div>
        <div id="tsnap-title">${M ? escapeHtml(M.report.title) : 'Report a problem'}</div>
        <div id="tsnap-sub">We'll attach this page's context to help us fix it faster.</div>
      </div>
      <button id="tsnap-close" aria-label="Close">${SVG_CLOSE}</button>
    </div>
    <div id="tsnap-body">
      <div id="tsnap-summary">
        <div id="tsnap-meta">
          <div class="tsnap-kv"><span class="k">Status</span><span class="v"><span class="tsnap-pill"><i></i>${escapeHtml(st)}</span></span></div>
          ${linkRow}
          <div class="tsnap-kv"><span class="k">Page</span><span class="v" title="${escapeHtml(url)}">${escapeHtml(url)}</span></div>
          <div class="tsnap-kv"><span class="k">When</span><span class="v">${escapeHtml(when)}</span></div>
        </div>
        <details id="tsnap-details"><summary>Technical details</summary><pre id="tsnap-pre"></pre></details>
        <div class="tsnap-field">
          <label class="tsnap-lbl" for="tsnap-notes">What happened?</label>
          <textarea id="tsnap-notes" placeholder="${M ? escapeHtml(M.report.notesPlaceholder) : 'Add any steps to reproduce or extra notes…'}"></textarea>
        </div>
      </div>
      <div id="tsnap-shotwrap">
        <label class="tsnap-lbl">Screenshot</label>
        <div id="tsnap-sshot"></div>
      </div>
    </div>
    <div id="tsnap-actions"></div>`;

  (box.querySelector('#tsnap-pre') as HTMLElement).textContent = snapshotJsonText;

  const sshot = box.querySelector('#tsnap-sshot') as HTMLElement;
  let currentShot: Blob | null = null;
  const renderImg = (src: string) => { sshot.innerHTML = ''; const img = doc.createElement('img'); img.src = src; img.alt = 'Screenshot preview'; sshot.append(img); };
  const renderEmpty = () => { sshot.innerHTML = `<div class="tsnap-empty">${SVG_IMAGE}<span>No screenshot yet — capture the page to attach it.</span></div>`; };
  if (opts.screenshotUrl) renderImg(opts.screenshotUrl); else renderEmpty();

  const shotwrap = box.querySelector('#tsnap-shotwrap') as HTMLElement;
  if (opts.onCaptureScreen) {
    const cap = doc.createElement('button'); cap.id = 'tsnap-capture'; cap.type = 'button';
    cap.innerHTML = `${SVG_CAPTURE}<span>${opts.screenshotUrl ? (M ? escapeHtml(M.report.retake) : 'Retake screenshot') : (M ? escapeHtml(M.report.capture) : 'Capture screen')}</span>`;
    const hint = doc.createElement('div'); hint.className = 'tsnap-hint'; hint.textContent = "Pixel-perfect — you'll pick this tab to share.";
    shotwrap.append(cap, hint);
    cap.onclick = async () => {
      cap.disabled = true; cap.innerHTML = `${SVG_SPINNER}<span>Capturing…</span>`;
      let shot: Blob | null = null;
      try { shot = await withWidgetHidden(() => opts.onCaptureScreen!()); } catch {}
      cap.disabled = false; cap.innerHTML = `${SVG_CAPTURE}<span>${M ? escapeHtml(M.report.retake) : 'Retake screenshot'}</span>`;
      if (shot) { currentShot = shot; renderImg(URL.createObjectURL(shot)); }
    };
  }

  const actions = box.querySelector('#tsnap-actions') as HTMLElement;
  const cancel = btn(M ? M.report.cancel : 'Cancel'); const submit = btn(M ? M.report.submit : 'Submit report', true);
  actions.append(cancel, submit);

  overlay.setAttribute('dir', dir);
  overlay.appendChild(box);
  doc.body.appendChild(overlay);
  const notes = box.querySelector('#tsnap-notes') as HTMLTextAreaElement;
  setTimeout(() => notes.focus(), 60);

  function btn(text: string, primary = false) { const b = doc.createElement('button'); b.className = 'tsnap-btn' + (primary ? ' primary' : ''); b.textContent = text; return b; }
  function cleanup() { overlay.remove(); style.remove(); doc.removeEventListener('keydown', onKey); }
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { cleanup(); opts.onCancel(); } };
  doc.addEventListener('keydown', onKey);
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) { cleanup(); opts.onCancel(); } });
  (box.querySelector('#tsnap-close') as HTMLElement).onclick = () => { cleanup(); opts.onCancel(); };
  cancel.onclick = () => { cleanup(); opts.onCancel(); };
  submit.onclick = () => { const t = (notes.value || '').trim(); cleanup(); opts.onSubmit(t, currentShot); };
}

/* ---------- support ticket form ---------- */

export interface TicketCategoryUI {
  code: string;
  label: string;
  subtypes: { code: string; label: string }[];
}

export interface TicketFormOptions {
  categories: TicketCategoryUI[];
  prefill?: { category?: string; subtype?: string; title?: string; description?: string; linkLabel?: string };
  /** Allow image attachments. Default: true. */
  allowAttachments?: boolean;
  /** When provided, shows a "Capture screen" button that adds the screenshot as an attachment. */
  onCaptureScreen?: () => Promise<Blob | null>;
  onSubmit: (data: { category: string; subtype?: string; title: string; description?: string; attachments?: Blob[] }) => void;
  onCancel: () => void;
  colorPreset?: ColorPreset;
  theme?: 'light' | 'dark';
  accent?: string;
  palette?: Partial<PaletteTokens>;
  messages?: SnapMessages;
  dir?: 'ltr' | 'rtl';
}

/** Typed cross-system ticket creation form (Category + Subtype + Title + Description + optional image). */
export function openTicketForm(opts: TicketFormOptions) {
  ensureFonts();
  const doc = document;
  const p = resolvePalette(opts.colorPreset, opts.theme, opts.accent, opts.palette);
  const overlay = doc.createElement('div');
  overlay.id = 'tsnap-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');

  const dir = opts.dir || doc.documentElement.getAttribute('dir') || (getComputedStyle(doc.body).direction || 'ltr');
  const M = opts.messages;
  const caret = encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${p.muted}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>`);
  const allowAttach = opts.allowAttachments !== false;

  const css = modalChrome(p, 560) + `
  #tsnap-form{display:grid;gap:16px;padding:20px 22px;overflow:auto}
  #tsnap-form .tsnap-field{display:grid;gap:8px}
  #tsnap-form .tsnap-lbl{font-family:${MONO};font-size:10.5px;font-weight:500;letter-spacing:.14em;text-transform:uppercase;color:${p.muted}}
  #tsnap-form select,#tsnap-form input,#tsnap-form textarea{width:100%;background:${p.surface};color:inherit;
    border:1px solid ${p.border};border-radius:13px;padding:12px 14px;font-family:${BODY};font-size:14px;
    backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);
    transition:border-color .16s ease,box-shadow .16s ease}
  #tsnap-form textarea{min-height:100px;resize:vertical;line-height:1.5}
  #tsnap-form select{appearance:none;cursor:pointer;padding-inline-end:38px;
    background-image:url("data:image/svg+xml,${caret}");background-repeat:no-repeat;background-position:right 13px center;background-size:14px}
  [dir="rtl"] #tsnap-form select{background-position:left 13px center}
  #tsnap-form select:focus,#tsnap-form input:focus,#tsnap-form textarea:focus{outline:none;border-color:var(--ac);
    box-shadow:0 0 0 4px color-mix(in srgb,var(--ac) 14%,transparent)}
  #tsnap-form input::placeholder,#tsnap-form textarea::placeholder{color:${p.faint}}
  .tsnap-row2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  @media (max-width:480px){.tsnap-row2{grid-template-columns:1fr}}
  #tsnap-linkrow{display:flex;align-items:center;gap:8px;font-family:${MONO};font-size:11.5px;color:${p.muted};
    background:${p.surface};border:1px solid ${p.border};border-radius:11px;padding:11px 13px;
    backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}
  #tsnap-linkrow b{color:${p.ink};font-weight:500}

  /* attachment dropzone */
  .tsnap-drop{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;
    border:1.5px dashed color-mix(in srgb,var(--ac) 36%,${p.border});background:${p.surface};border-radius:13px;
    padding:22px 16px;cursor:pointer;color:${p.muted};text-align:center;
    backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);
    transition:background .2s ease,border-color .2s ease,transform .2s ease}
  .tsnap-drop:hover,.tsnap-drop.drag{background:color-mix(in srgb,var(--ac) 9%,${p.surface});border-color:var(--ac);color:${p.ink}}
  .tsnap-drop .ic{width:42px;height:42px;border-radius:12px;display:grid;place-items:center;color:var(--ac);
    background:color-mix(in srgb,var(--ac) 12%,transparent);
    border:1px solid color-mix(in srgb,var(--ac) 24%,${p.border})}
  .tsnap-drop .ic svg{width:22px;height:22px}
  .tsnap-drop .t{font-family:${BODY};font-size:13.5px;font-weight:500;color:${p.ink}}
  .tsnap-drop .d{font-family:${MONO};font-size:10.5px;letter-spacing:.08em;color:${p.faint};text-transform:uppercase}
  .tsnap-drop input{position:absolute;inset:0;opacity:0;cursor:pointer}

  .tsnap-or{display:flex;align-items:center;gap:10px;font-family:${MONO};font-size:10px;letter-spacing:.14em;
    text-transform:uppercase;color:${p.faint}}
  .tsnap-or::before,.tsnap-or::after{content:"";flex:1;height:1px;background:${p.border}}
  .tsnap-capbtn{width:100%;display:inline-flex;align-items:center;justify-content:center;gap:8px;
    border:1px dashed color-mix(in srgb,var(--ac) 45%,${p.border});background:color-mix(in srgb,var(--ac) 7%,transparent);color:var(--ac);
    border-radius:13px;padding:11px 12px;font-family:${BODY};font-size:13px;font-weight:500;cursor:pointer;
    transition:background .18s ease,border-color .18s ease}
  .tsnap-capbtn svg{width:16px;height:16px}
  .tsnap-capbtn:hover:not(:disabled){background:color-mix(in srgb,var(--ac) 12%,transparent);border-style:solid}
  .tsnap-capbtn:disabled{opacity:.6;cursor:default}

  .tsnap-thumbs{display:grid;grid-template-columns:repeat(auto-fill,minmax(96px,1fr));gap:8px}
  .tsnap-thumb{position:relative;aspect-ratio:1;border:1px solid ${p.border};border-radius:11px;overflow:hidden;background:${p.surface};
    backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}
  .tsnap-thumb img{width:100%;height:100%;object-fit:cover;display:block}
  .tsnap-thumb .x{position:absolute;top:6px;right:6px;width:24px;height:24px;border-radius:8px;border:0;
    background:rgba(0,0,0,0.58);color:#fff;display:grid;place-items:center;cursor:pointer;
    backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);opacity:0;transition:opacity .2s ease}
  .tsnap-thumb .x svg{width:14px;height:14px}
  .tsnap-thumb:hover .x{opacity:1}
  .tsnap-thumb .name{position:absolute;left:0;right:0;bottom:0;padding:5px 7px;font-family:${MONO};font-size:9.5px;
    background:linear-gradient(to top,rgba(0,0,0,0.62),transparent);color:#fff;text-overflow:ellipsis;
    white-space:nowrap;overflow:hidden;letter-spacing:.04em}

  #tsnap-empty{display:grid;justify-items:center;text-align:center;gap:14px;padding:36px 26px}
  #tsnap-empty .ic{width:60px;height:60px;border-radius:17px;display:grid;place-items:center;color:var(--ac);
    background:color-mix(in srgb,var(--ac) 12%,transparent);border:1.5px dashed color-mix(in srgb,var(--ac) 38%,${p.border})}
  #tsnap-empty .ic svg{width:28px;height:28px}
  #tsnap-empty .t{font-family:${DISP};font-size:22px;font-weight:600;letter-spacing:-.015em}
  #tsnap-empty .d{font-size:13.5px;color:${p.muted};line-height:1.5;max-width:340px}
  `;
  const style = doc.createElement('style'); style.textContent = css; doc.head.appendChild(style);

  const box = doc.createElement('div'); box.id = 'tsnap-box'; box.setAttribute('dir', dir);
  box.innerHTML = `
    ${auroraHtml()}
    <div id="tsnap-hd">
      <div id="tsnap-hd-ic">${SVG_CHAT}</div>
      <div id="tsnap-hd-tx">
        <div id="tsnap-kick">Support ticket</div>
        <div id="tsnap-title">${M ? escapeHtml(M.support.title) : 'Get support'}</div>
        <div id="tsnap-sub">Tell us what you need — we'll open a ticket and follow up.</div>
      </div>
      <button id="tsnap-close" aria-label="Close">${SVG_CLOSE}</button>
    </div>
    <div id="tsnap-form"></div>
    <div id="tsnap-actions"></div>`;

  const form = box.querySelector('#tsnap-form') as HTMLElement;

  // No configured ticket types → distinct support empty-state (NOT the report flow).
  if (!opts.categories.length) {
    form.innerHTML = `<div id="tsnap-empty">
      <div class="ic">${SVG_CHAT}</div>
      <div class="t">No support categories yet</div>
      <div class="d">An admin needs to set up ticket types for this project before you can open a support ticket.</div>
    </div>`;
    const actions0 = box.querySelector('#tsnap-actions') as HTMLElement;
    const done = doc.createElement('button'); done.className = 'tsnap-btn primary'; done.textContent = 'Close';
    actions0.append(done);
    overlay.append(box); doc.body.append(overlay);
    const cleanup0 = () => { overlay.remove(); style.remove(); doc.removeEventListener('keydown', onKey0); };
    const onKey0 = (e: KeyboardEvent) => { if (e.key === 'Escape') { cleanup0(); opts.onCancel(); } };
    doc.addEventListener('keydown', onKey0);
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) { cleanup0(); opts.onCancel(); } });
    (box.querySelector('#tsnap-close') as HTMLElement).onclick = () => { cleanup0(); opts.onCancel(); };
    done.onclick = () => { cleanup0(); opts.onCancel(); };
    return;
  }

  const catSel = doc.createElement('select');
  for (const c of opts.categories) {
    const o = doc.createElement('option'); o.value = c.code; o.textContent = c.label; catSel.append(o);
  }
  const subSel = doc.createElement('select');
  const titleInput = doc.createElement('input'); titleInput.type = 'text'; titleInput.placeholder = M ? M.support.titlePlaceholder : 'Short, descriptive title';
  const descInput = doc.createElement('textarea'); descInput.placeholder = M ? M.support.descPlaceholder : 'Describe the issue…';

  const subField = mkField('Subtype', subSel);
  const refillSubtypes = () => {
    const cat = opts.categories.find(c => c.code === catSel.value);
    subSel.innerHTML = '';
    const none = doc.createElement('option'); none.value = ''; none.textContent = '(none)'; subSel.append(none);
    for (const s of (cat?.subtypes ?? [])) {
      const o = doc.createElement('option'); o.value = s.code; o.textContent = s.label; subSel.append(o);
    }
    if (opts.prefill?.subtype) subSel.value = opts.prefill.subtype;
    subField.style.display = subSel.options.length > 1 ? '' : 'none';
  };
  catSel.onchange = refillSubtypes;

  if (opts.prefill?.category) catSel.value = opts.prefill.category;
  refillSubtypes();
  if (opts.prefill?.title) titleInput.value = opts.prefill.title;
  if (opts.prefill?.description) descInput.value = opts.prefill.description;

  function mkField(text: string, el: HTMLElement) {
    const f = doc.createElement('div'); f.className = 'tsnap-field';
    const l = doc.createElement('label'); l.className = 'tsnap-lbl'; l.textContent = text;
    f.append(l, el); return f;
  }
  const row2 = doc.createElement('div'); row2.className = 'tsnap-row2';
  row2.append(mkField('Category', catSel), subField);
  form.append(row2, mkField('Title', titleInput), mkField('Description', descInput));

  // attachments
  let attachments: File[] = [];
  const objectUrls = new Map<File, string>();
  const releaseUrls = () => { objectUrls.forEach(u => URL.revokeObjectURL(u)); objectUrls.clear(); };

  if (allowAttach) {
    const attachField = doc.createElement('div'); attachField.className = 'tsnap-field';
    const lbl = doc.createElement('label'); lbl.className = 'tsnap-lbl'; lbl.textContent = 'Attach images (optional)';
    attachField.append(lbl);

    const drop = doc.createElement('label'); drop.className = 'tsnap-drop';
    drop.innerHTML = `
      <span class="ic">${SVG_UPLOAD}</span>
      <span class="t">Drop an image or click to browse</span>
      <span class="d">PNG · JPG · WebP — up to 5 files</span>`;
    const fileInput = doc.createElement('input');
    fileInput.type = 'file'; fileInput.accept = 'image/*'; fileInput.multiple = true;
    drop.append(fileInput);
    attachField.append(drop);

    let capBtn: HTMLButtonElement | null = null;
    if (opts.onCaptureScreen) {
      const or = doc.createElement('div'); or.className = 'tsnap-or'; or.textContent = 'or';
      capBtn = doc.createElement('button'); capBtn.type = 'button'; capBtn.className = 'tsnap-capbtn';
      capBtn.innerHTML = `${SVG_CAPTURE}<span>${M ? escapeHtml(M.support.capture) : 'Capture screen'}</span>`;
      attachField.append(or, capBtn);
    }

    const grid = doc.createElement('div'); grid.className = 'tsnap-thumbs'; grid.style.display = 'none';
    attachField.append(grid);
    form.append(attachField);

    const urlFor = (f: File) => {
      let u = objectUrls.get(f);
      if (!u) { u = URL.createObjectURL(f); objectUrls.set(f, u); }
      return u;
    };
    const removeAt = (i: number) => {
      const [removed] = attachments.splice(i, 1);
      if (removed) { const u = objectUrls.get(removed); if (u) { URL.revokeObjectURL(u); objectUrls.delete(removed); } }
      renderThumbs();
    };
    const renderThumbs = () => {
      grid.innerHTML = '';
      attachments.forEach((f, i) => {
        const t = doc.createElement('div'); t.className = 'tsnap-thumb';
        const img = doc.createElement('img'); img.src = urlFor(f); img.alt = f.name;
        const x = doc.createElement('button'); x.type = 'button'; x.className = 'x'; x.innerHTML = SVG_TRASH; x.setAttribute('aria-label', 'Remove attachment');
        const name = doc.createElement('div'); name.className = 'name'; name.textContent = f.name;
        x.onclick = (e) => { e.preventDefault(); e.stopPropagation(); removeAt(i); };
        t.append(img, x, name); grid.append(t);
      });
      grid.style.display = attachments.length ? '' : 'none';
    };

    const addFiles = (files: FileList | File[]) => {
      const incoming = Array.from(files).filter(f => f.type.startsWith('image/'));
      attachments = [...attachments, ...incoming].slice(0, 5);
      renderThumbs();
    };

    fileInput.onchange = () => { if (fileInput.files) addFiles(fileInput.files); fileInput.value = ''; };

    if (capBtn && opts.onCaptureScreen) {
      let shotN = 0;
      capBtn.onclick = async () => {
        const btnEl = capBtn!;
        btnEl.disabled = true; btnEl.innerHTML = `${SVG_SPINNER}<span>Capturing…</span>`;
        let shot: Blob | null = null;
        try { shot = await withWidgetHidden(() => opts.onCaptureScreen!()); } catch {}
        btnEl.disabled = false; btnEl.innerHTML = `${SVG_CAPTURE}<span>${M ? escapeHtml(M.support.capture) : 'Capture screen'}</span>`;
        if (shot) {
          const file = new File([shot], `screenshot-${++shotN}.png`, { type: shot.type || 'image/png' });
          addFiles([file]);
        }
      };
    }

    drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('drag'); });
    drop.addEventListener('dragleave', () => drop.classList.remove('drag'));
    drop.addEventListener('drop', (e) => {
      e.preventDefault(); drop.classList.remove('drag');
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    });
  }

  if (opts.prefill?.linkLabel) {
    const link = doc.createElement('div'); link.id = 'tsnap-linkrow'; link.innerHTML = `Linked to <b>${escapeHtml(opts.prefill.linkLabel)}</b>`;
    form.append(link);
  }

  const actions = box.querySelector('#tsnap-actions') as HTMLElement;
  const mkBtn = (text: string, primary = false) => { const b = doc.createElement('button'); b.className = 'tsnap-btn' + (primary ? ' primary' : ''); b.textContent = text; return b; };
  const cancel = mkBtn(M ? M.support.cancel : 'Cancel'); const submit = mkBtn(M ? M.support.submit : 'Submit ticket', true);
  actions.append(cancel, submit);

  overlay.setAttribute('dir', dir);
  overlay.append(box); doc.body.append(overlay);
  setTimeout(() => titleInput.focus(), 60);

  const cleanup = () => { overlay.remove(); style.remove(); doc.removeEventListener('keydown', onKey); releaseUrls(); };
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { cleanup(); opts.onCancel(); } };
  doc.addEventListener('keydown', onKey);
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) { cleanup(); opts.onCancel(); } });
  (box.querySelector('#tsnap-close') as HTMLElement).onclick = () => { cleanup(); opts.onCancel(); };
  cancel.onclick = () => { cleanup(); opts.onCancel(); };
  submit.onclick = () => {
    const t = (titleInput.value || '').trim();
    if (!catSel.value) { catSel.focus(); return; }
    if (!t) { titleInput.focus(); return; }
    const out = attachments.slice();
    cleanup();
    opts.onSubmit({
      category: catSel.value,
      subtype: subSel.value || undefined,
      title: t,
      description: (descInput.value || '').trim() || undefined,
      attachments: out.length ? out : undefined
    });
  };
}

/* ---------- toast ---------- */

export interface ToastOptions {
  colorPreset?: ColorPreset;
  theme?: 'light' | 'dark';
  accent?: string;
  palette?: Partial<PaletteTokens>;
}

export function toast(msg: string, opts: ToastOptions = {}) {
  ensureFonts();
  const p = resolvePalette(opts.colorPreset, opts.theme, opts.accent, opts.palette);
  let host = document.getElementById('tsnap-toasts');
  if (!host) {
    host = document.createElement('div'); host.id = 'tsnap-toasts';
    host.setAttribute('style', [
      'position:fixed', 'left:50%', 'bottom:24px', 'transform:translateX(-50%)',
      'z-index:2147483647', 'display:flex', 'flex-direction:column', 'gap:8px', 'align-items:center',
      'pointer-events:none', `font-family:${BODY}`
    ].join(';'));
    document.body.appendChild(host);
  }
  if (!document.getElementById('tsnap-toast-style')) {
    const s = document.createElement('style'); s.id = 'tsnap-toast-style';
    s.textContent = `@keyframes tsnap-toast-in{from{opacity:0;transform:translateY(10px) scale(.96)}to{opacity:1;transform:none}}
      @keyframes tsnap-toast-out{to{opacity:0;transform:translateY(8px) scale(.97)}}`;
    document.head.appendChild(s);
  }
  const t = document.createElement('div');
  t.setAttribute('style', [
    'pointer-events:auto', 'display:inline-flex', 'align-items:center', 'gap:10px',
    'max-width:min(440px,90vw)', `background:${p.glassBg}`, `color:${p.ink}`,
    `border:1px solid ${p.border}`, 'border-radius:14px', 'padding:11px 17px', 'font-size:13px', 'font-weight:500',
    'backdrop-filter:blur(20px) saturate(180%)', '-webkit-backdrop-filter:blur(20px) saturate(180%)',
    `box-shadow:${p.shadow}`, 'animation:tsnap-toast-in .3s cubic-bezier(.2,.9,.25,1.2)'
  ].join(';'));
  const dot = document.createElement('span');
  dot.setAttribute('style', `flex:none;width:8px;height:8px;border-radius:50%;background:${p.accent};box-shadow:0 0 10px ${p.accent}80`);
  const span = document.createElement('span'); span.textContent = msg;
  t.append(dot, span);
  host.appendChild(t);
  setTimeout(() => { t.style.animation = 'tsnap-toast-out .24s ease forwards'; setTimeout(() => t.remove(), 240); }, 2800);
}
